/**
 * useHomeSLDemoCrmBoardLogic
 *
 * Pure logic hook — state machine, orbital bridge, actions.
 * Use the companion component for rendering.
 *
 * @generated from schema
 * @trait HomeSLDemoCrmBoard
 */

import { useReducer, useCallback, useEffect, useRef, useMemo } from 'react';
import { useOrbitalBridge } from '@/hooks/useOrbitalBridge';
import type { EntityRow, EventPayload, EventPayloadValue, FieldValue } from '@almadar/core';
function getNestedValue(obj: EventPayload | EventPayloadValue | FieldValue | Record<string, FieldValue | undefined> | null | undefined, path: string): FieldValue | undefined {
  if (obj === null || obj === undefined || !path) return undefined;
  if (typeof obj !== 'object') return undefined;
  if (!path.includes('.') && !Array.isArray(obj)) {
    return (obj as Record<string, FieldValue | undefined>)[path];
  }
  let value: EventPayload | EventPayloadValue | FieldValue | Record<string, FieldValue | undefined> | null | undefined = obj;
  for (const part of path.split('.')) {
    if (value === null || value === undefined || typeof value !== 'object') return undefined;
    if (Array.isArray(value)) {
      if (!/^\d+$/.test(part)) return undefined;
      value = (value as readonly (EventPayloadValue | FieldValue | null | undefined)[])[Number(part)];
      continue;
    }
    value = (value as Record<string, FieldValue | undefined>)[part];
  }
  return value as FieldValue | undefined;
}
import type { EventResponse, HomeSLDemoCrmDeal, HomeSLDemoCrmBoardEventPayloadMap } from '@app/shared';
import { useUIEvents, claimDelivery } from '@almadar/ui/hooks';
import { registerTraitSnapshot, type TraitStateSnapshot } from '@almadar/ui/lib';
import { createCommandSendPump } from '@almadar/ui/lib';
import type { BusEvent } from '@almadar/core';
import { useTranslate } from '@almadar/ui/hooks';

// ============================================================================
// State Machine
// ============================================================================

export type HomeSLDemoCrmBoardState =
  | 'loading'
  | 'viewing_board'
  | 'viewing_card'
  | 'adding'
  | 'error';

export type HomeSLDemoCrmBoardEvent =
  | 'INIT'
  | 'BoardItemsLoaded'
  | 'BoardItemsLoadFailed'
  | 'BoardItemsSaved'
  | 'BoardItemDeleted'
  | 'BoardItemsSaveFailed'
  | 'OPEN_CARD'
  | 'ADD_CARD'
  | 'MOVE_CARD'
  | 'REORDER_CARD'
  | 'REORDER_POSITION'
  | 'REFETCH_QUERY'
  | 'REFETCH_FILTER'
  | 'REFETCH_PAGE'
  | 'CLOSE_CARD'
  | 'DELETE_CARD'
  | 'SAVE_CARD'
  | 'CANCEL_ADD'
  | 'EDIT_CARD'
  | 'TOUR_STEP'
  | 'TOUR_RESET';

type HomeSLDemoCrmBoardSlot = 'main' | 'toast';
interface HomeSLDemoCrmBoardInternalState {
  machineState: HomeSLDemoCrmBoardState;
  data: Record<string, EntityRow[]>;
  lastPayload: EventPayload | undefined;
  lastEvent: HomeSLDemoCrmBoardEvent | null;
  slotWriter: Partial<Record<HomeSLDemoCrmBoardSlot, HomeSLDemoCrmBoardEvent | null>>;
  loading: boolean;
  pendingEvent: HomeSLDemoCrmBoardEvent | null;
  pendingFrom: string | null;
  error: string | null;
  fields: Partial<HomeSLDemoCrmDeal>;
}

type HomeSLDemoCrmBoardAction =
  | { type: 'EVENT_SUCCESS'; event: HomeSLDemoCrmBoardEvent; newState: string; data: Record<string, EntityRow[]>; payload?: EventPayload; fields?: Record<string, EventPayloadValue | undefined>; renderBearing?: boolean; renderWrite?: HomeSLDemoCrmBoardSlot[]; renderClear?: HomeSLDemoCrmBoardSlot[] }
  | { type: 'EVENT_ERROR'; error: string }
  | { type: 'RESET_ERROR' }
  | { type: 'START_EVENT'; event: HomeSLDemoCrmBoardEvent; from: string }
  | { type: 'END_EVENT' }
  | { type: 'SET_FIELD'; field: keyof HomeSLDemoCrmDeal & string; value: EventPayloadValue | undefined }
  | { type: 'TICK'; updates: Record<string, EventPayloadValue | undefined>; collection: string };

function homeSLDemoCrmBoardReducer(
  state: HomeSLDemoCrmBoardInternalState,
  action: HomeSLDemoCrmBoardAction
): HomeSLDemoCrmBoardInternalState {
  switch (action.type) {
    case 'START_EVENT':
      return { ...state, loading: true, pendingEvent: action.event, pendingFrom: action.from };
    case 'END_EVENT':
      return { ...state, loading: false, pendingEvent: null, pendingFrom: null };
    case 'EVENT_SUCCESS':
      return {
        machineState: action.newState as HomeSLDemoCrmBoardState,
        data: { ...state.data, ...action.data },
        lastPayload: action.renderBearing ? (action.payload ?? state.lastPayload) : state.lastPayload,
        lastEvent: action.renderBearing ? action.event : state.lastEvent,
        slotWriter: {
          ...state.slotWriter,
          ...Object.fromEntries((action.renderWrite ?? []).map((s) => [s, action.event] as const)),
          ...Object.fromEntries((action.renderClear ?? []).map((s) => [s, null] as const)),
        },
        loading: false,
        pendingEvent: null,
        pendingFrom: null,
        error: null,
        fields: { ...state.fields, ...(action.fields ?? {}) } as Partial<HomeSLDemoCrmDeal>,
      };
    case 'EVENT_ERROR':
      return { ...state, loading: false, pendingEvent: null, pendingFrom: null, error: action.error };
    case 'RESET_ERROR':
      return { ...state, error: null };
    case 'SET_FIELD': {
      // Back-compat for non-codegen consumers that still dispatch
      // SET_FIELD directly. Codegen folds field updates into
      // EVENT_SUCCESS so transitions commit in a single render.
      return {
        ...state,
        fields: { ...state.fields, [action.field]: action.value } as Partial<HomeSLDemoCrmDeal>,
      };
    }
    case 'TICK': {
      // Batch-update entity fields for tick effects. A [runtime]-linked
      // trait's row lives in the `fields` mirror (transitions write it,
      // the view reads it), so updates ALWAYS merge there; the
      // `data` collection row is updated too when one exists.
      const tickFields = { ...state.fields, ...action.updates } as Partial<HomeSLDemoCrmDeal>;
      const arr = state.data[action.collection];
      const target = arr?.[0];
      if (!target) return { ...state, fields: tickFields };
      const updated = { ...target, ...action.updates } as EntityRow;
      return {
        ...state,
        data: { ...state.data, [action.collection]: [updated, ...arr.slice(1)] },
        fields: tickFields,
      };
    }
    default:
      return state;
  }
}

const initialState: HomeSLDemoCrmBoardInternalState = {
  machineState: 'loading',
  data: {},
  lastPayload: undefined,
  lastEvent: null,
  slotWriter: {},
  loading: false,
  pendingEvent: null,
  pendingFrom: null,
  error: null,
  fields: {},
};

// ============================================================================
// Hook
// ============================================================================

export interface HomeSLDemoCrmBoardConfig {
  linkedEntity?: string;
  entityId?: string;
  routeParams?: Record<string, string | undefined>;
  initPayload?: EventPayload;
  addPlacement: 'floating' | 'inline' | 'none';
  boardLook: 'columns' | 'kanban-classic';
  bodyContent: { children: Array<Array<Array<number | string | string[]> | string | { children: FieldValue[]; gap: string; type: string } | { children: { entity: string; event: string; filters: string; look: string; type: string }[]; direction: string; gap: string; type: string }> | Array<string | string[] | { action: string; icon: string; label: string; type: string; variant: string } | { children: FieldValue[]; gap: string; type: string }> | Array<string | string[] | { children: FieldValue[]; gap: string; type: string } | { children: { action: string; icon: string; label: string; size: string; type: string; variant: string }[]; direction: string; gap: string; justify: string; type: string }> | Array<string | { children: Array<{ align: string; children: Array<{ content: string; type: string; variant: string } | { name: string; size: string; type: string }>; direction: string; gap: string; type: string } | { type: string }>; direction: string; gap: string; type: string } | { children: FieldValue[]; gap: string; type: string }> | Array<string | { children: FieldValue[]; gap: string; type: string } | { children: { className: string; clearable: boolean; event: string; placeholder: string; type: string }[]; direction: string; gap: string; type: string }> | { cols: string; dndRoot: string; entity: string; fields: FieldValue[]; gap: string; renderItem: Array<string | string[] | { children: { children: Array<{ accepts: string[]; dragGroup: string[]; dropEvent: string; entity: string; fields: FieldValue[]; gap: string; itemEnter: string; positionEvent: string; renderItem: Array<string | { children: { children: Array<{ align: string; children: { action: string; actionPayload: { description: string; id: string; notes: string; row: string; stage: string; title: string }; icon: string; label: string; type: string; variant: string }[]; direction: string; gap: string; type: string } | { color: string; content: string; type: string; variant: string } | { content: string; type: string; variant: string }>; direction: string; gap: string; type: string }[]; look: string; type: string }>; reorderEvent: string; sortable: string; type: string } | { align: string; children: Array<{ content: Array<Array<number | string | string[]> | string | string[]>; type: string; variant: string } | { label: string; type: string; variant: string } | { name: string; type: string }>; direction: string; gap: string; type: string }>; direction: string; gap: string; type: string }[]; look: string; type: string }>; type: string }>; direction: string; gap: string; type: string };
  bodySearch: boolean;
  cardActions: { event?: string; icon?: string; label: string; roles?: string[]; variant?: 'primary' | 'secondary' | 'ghost' | 'danger' }[];
  cardActionsContent: { align: string; children: Array<Array<Array<Array<Array<Array<Array<FieldValue[] | string> | string> | number | string> | Array<Array<FieldValue[] | string> | string> | string> | string> | string> | Array<string | { action: string; actionPayload: { id: string }; icon: string; label: string; type: string; variant: string }> | string>; direction: string; gap: string; type: string };
  cardDescriptionBinding?: string;
  cardEnter: 'none' | 'fade' | 'rise' | 'scale' | 'slide';
  cardIdBinding: string;
  cardLook: 'elevated' | 'flat-bordered' | 'borderless-divider' | 'ticket' | 'invoice' | 'chip' | 'tile-image-first';
  cardNotesBinding: string;
  cardStageBinding: string;
  cardTitleBinding: string;
  columnLabels?: string[];
  columns: { key: string; label: string; variant: string }[];
  draggable: boolean;
  filterBarLook: 'toolbar' | 'chips' | 'pills' | 'popover-trigger' | 'inline-column-header';
  filters: { field: string; label?: string; options?: string[] }[];
  formFields: string[];
  gridCols: number;
  groupByField: string;
  include: string[];
  kanbanClassicBodyContent: { children: Array<Array<Array<number | string | string[]> | string | { children: FieldValue[]; gap: string; type: string } | { children: { entity: string; event: string; filters: string; look: string; type: string }[]; className: string; direction: string; gap: string; type: string }> | Array<string | string[] | { action: string; icon: string; label: string; type: string; variant: string } | { children: FieldValue[]; gap: string; type: string }> | Array<string | string[] | { children: FieldValue[]; gap: string; type: string } | { children: { action: string; icon: string; label: string; size: string; type: string; variant: string }[]; direction: string; gap: string; justify: string; type: string }> | Array<string | { children: Array<{ align: string; children: Array<{ content: string; type: string; variant: string } | { name: string; size: string; type: string }>; className: string; direction: string; gap: string; type: string } | { type: string }>; direction: string; gap: string; type: string } | { children: FieldValue[]; gap: string; type: string }> | Array<string | { children: FieldValue[]; gap: string; type: string } | { children: { className: string; clearable: boolean; event: string; placeholder: string; type: string }[]; className: string; direction: string; gap: string; type: string }> | { className: string; dndRoot: string; entity: string; fields: FieldValue[]; gap: string; minCardWidth: number; renderItem: Array<string | string[] | { children: { children: Array<{ accepts: string[]; className: string; dragGroup: string[]; dropEvent: string; entity: string; fields: FieldValue[]; gap: string; itemEnter: string; positionEvent: string; renderItem: Array<string | { children: { children: Array<{ align: string; children: Array<{ action: string; actionPayload: { description: string; id: string; notes: string; row: string; stage: string; title: string }; className: string; icon: string; label: string; size: string; type: string; variant: string } | { label: string; size: string; type: string; variant: string }>; className: string; direction: string; gap: string; type: string } | { className: string; color: string; content: string; type: string; variant: string } | { content: string; type: string; variant: string; weight: string }>; direction: string; gap: string; type: string }[]; className: string; look: string; padding: string; type: string }>; reorderEvent: string; sortable: string; type: string } | { align: string; children: Array<{ className: string; content: Array<Array<number | string | string[]> | string | string[]>; type: string; variant: string } | { label: string; size: string; type: string; variant: string } | { name: string; type: string }>; className: string; direction: string; gap: string; type: string }>; direction: string; gap: string; type: string }[]; className: string; look: string; padding: string; type: string }>; scrollX: boolean; type: string }>; className: string; direction: string; gap: string; type: string };
  pageSize: number;
  searchField: string;
  searchPlaceholder: string;
  showHeader: boolean;
  title: string;
  viewerRole: string;
}

const DEFAULT_HOME_SL_DEMO_CRM_BOARD_CONFIG = {
  addPlacement: "none",
  boardLook: "columns",
  bodyContent: { children: [["if", ["=", "@config.addPlacement", "inline"], { children: [{ action: "ADD_CARD", icon: "plus", label: "Add item", size: "sm", type: "button", variant: "secondary" }], direction: "horizontal", gap: "sm", justify: "end", type: "stack" }, { children: [], gap: "none", type: "stack" }], ["if", "@config.showHeader", { children: [{ align: "center", children: [{ name: "kanban-square", size: "lg", type: "icon" }, { content: "@config.title", type: "typography", variant: "h3" }], direction: "horizontal", gap: "sm", type: "stack" }, { type: "divider" }], direction: "vertical", gap: "md", type: "stack" }, { children: [], gap: "none", type: "stack" }], ["if", "@config.bodySearch", { children: [{ className: "w-full max-w-md", clearable: true, event: "REFETCH_QUERY", placeholder: "@config.searchPlaceholder", type: "search-input" }], direction: "horizontal", gap: "sm", type: "stack" }, { children: [], gap: "none", type: "stack" }], ["if", [">", ["array/len", "@config.filters"], 0], { children: [{ entity: "BoardView", event: "REFETCH_FILTER", filters: "@config.filters", look: "@config.filterBarLook", type: "filter-group" }], direction: "horizontal", gap: "sm", type: "stack" }, { children: [], gap: "none", type: "stack" }], { cols: "@config.gridCols", dndRoot: "@config.draggable", entity: "@entity.boards", fields: [], gap: "md", renderItem: ["fn", ["col", "colIndex"], { children: [{ children: [{ align: "center", children: [{ name: "@col.icon", type: "icon" }, { content: ["if", ["==", ["array/len", "@config.columnLabels"], 0], "@col.label", ["array/nth", "@config.columnLabels", "@colIndex"]], type: "typography", variant: "h4" }, { label: "@col.count", type: "badge", variant: "@col.variant" }], direction: "horizontal", gap: "xs", type: "stack" }, { accepts: ["if", "@config.draggable", "*", ""], dragGroup: ["if", "@config.draggable", "@col.key", ""], dropEvent: "MOVE_CARD", entity: "@col.items", fields: [], gap: "sm", itemEnter: "@config.cardEnter", positionEvent: "REORDER_POSITION", renderItem: ["fn", "item", { children: [{ children: [{ content: "@config.cardTitleBinding", type: "typography", variant: "h4" }, { color: "muted", content: "@config.cardDescriptionBinding", type: "typography", variant: "caption" }, { align: "center", children: [{ action: "OPEN_CARD", actionPayload: { description: "@config.cardDescriptionBinding", id: "@config.cardIdBinding", notes: "@config.cardNotesBinding", row: "@item", stage: "@config.cardStageBinding", title: "@config.cardTitleBinding" }, icon: "arrow-right", label: "Open", type: "button", variant: "ghost" }], direction: "horizontal", gap: "xs", type: "stack" }], direction: "vertical", gap: "xs", type: "stack" }], look: "@config.cardLook", type: "card" }], reorderEvent: "REORDER_CARD", sortable: "@config.draggable", type: "data-list" }], direction: "vertical", gap: "sm", type: "stack" }], look: "@config.cardLook", type: "card" }], type: "data-grid" }, ["if", ["=", "@config.addPlacement", "floating"], { action: "ADD_CARD", icon: "plus", label: "Add item", type: "floating-action-button", variant: "primary" }, { children: [], gap: "none", type: "stack" }]], direction: "vertical", gap: "md", type: "stack" },
  bodySearch: false,
  cardActions: [],
  cardActionsContent: { align: "center", children: ["array/map", ["array/filter", "@config.cardActions", ["fn", "action", ["or", ["=", ["array/len", ["object/get", "@action", "roles", []]], 0], ["array/includes", ["object/get", "@action", "roles", []], "@config.viewerRole"]]]], ["fn", "a", { action: "@a.event", actionPayload: { id: "@entity.currentId" }, icon: "@a.icon", label: "@a.label", type: "button", variant: "@a.variant" }]], direction: "horizontal", gap: "sm", type: "stack" },
  cardEnter: "rise",
  cardIdBinding: "@item.id",
  cardLook: "ticket",
  cardNotesBinding: "@item.notes",
  cardStageBinding: "@item.stage",
  cardTitleBinding: "@item.title",
  columns: [{ key: "lead", label: "Lead", variant: "default" }, { key: "qualified", label: "Qualified", variant: "info" }, { key: "proposal", label: "Proposal", variant: "warning" }, { key: "won", label: "Won", variant: "success" }],
  draggable: false,
  filterBarLook: "toolbar",
  filters: [],
  formFields: ["title", "description", "stage", "notes"],
  gridCols: 4,
  groupByField: "stage",
  include: [],
  kanbanClassicBodyContent: { children: [["if", ["=", "@config.addPlacement", "inline"], { children: [{ action: "ADD_CARD", icon: "plus", label: "Add card", size: "sm", type: "button", variant: "secondary" }], direction: "horizontal", gap: "sm", justify: "end", type: "stack" }, { children: [], gap: "none", type: "stack" }], ["if", "@config.showHeader", { children: [{ align: "center", children: [{ name: "kanban-square", size: "xl", type: "icon" }, { content: "@config.title", type: "typography", variant: "h2" }], className: "px-card-md", direction: "horizontal", gap: "sm", type: "stack" }, { type: "divider" }], direction: "vertical", gap: "md", type: "stack" }, { children: [], gap: "none", type: "stack" }], ["if", "@config.bodySearch", { children: [{ className: "w-full max-w-md", clearable: true, event: "REFETCH_QUERY", placeholder: "@config.searchPlaceholder", type: "search-input" }], className: "px-card-md", direction: "horizontal", gap: "sm", type: "stack" }, { children: [], gap: "none", type: "stack" }], ["if", [">", ["array/len", "@config.filters"], 0], { children: [{ entity: "BoardView", event: "REFETCH_FILTER", filters: "@config.filters", look: "@config.filterBarLook", type: "filter-group" }], className: "px-card-md", direction: "horizontal", gap: "sm", type: "stack" }, { children: [], gap: "none", type: "stack" }], { className: "w-full pb-2", dndRoot: "@config.draggable", entity: "@entity.boards", fields: [], gap: "lg", minCardWidth: 300, renderItem: ["fn", ["col", "colIndex"], { children: [{ children: [{ align: "center", children: [{ name: "@col.icon", type: "icon" }, { className: "flex-1", content: ["if", ["==", ["array/len", "@config.columnLabels"], 0], "@col.label", ["array/nth", "@config.columnLabels", "@colIndex"]], type: "typography", variant: "h4" }, { label: "@col.count", size: "sm", type: "badge", variant: "primary" }], className: "p-card-md border-b border-[var(--color-border)] sticky top-0 bg-[var(--color-surface)]", direction: "horizontal", gap: "sm", type: "stack" }, { accepts: ["if", "@config.draggable", "*", ""], className: "p-card-md min-h-[120px]", dragGroup: ["if", "@config.draggable", "@col.key", ""], dropEvent: "MOVE_CARD", entity: "@col.items", fields: [], gap: "sm", itemEnter: "@config.cardEnter", positionEvent: "REORDER_POSITION", renderItem: ["fn", "item", { children: [{ children: [{ content: "@config.cardTitleBinding", type: "typography", variant: "body", weight: "medium" }, { className: "line-clamp-2", color: "muted", content: "@config.cardDescriptionBinding", type: "typography", variant: "caption" }, { align: "center", children: [{ label: "@config.cardStageBinding", size: "sm", type: "badge", variant: "@col.variant" }, { action: "OPEN_CARD", actionPayload: { description: "@config.cardDescriptionBinding", id: "@config.cardIdBinding", notes: "@config.cardNotesBinding", row: "@item", stage: "@config.cardStageBinding", title: "@config.cardTitleBinding" }, className: "ml-auto", icon: "arrow-right", label: "Open", size: "sm", type: "button", variant: "ghost" }], className: "pt-1", direction: "horizontal", gap: "xs", type: "stack" }], direction: "vertical", gap: "xs", type: "stack" }], className: "cursor-grab hover:shadow-lg transition-shadow", look: "@config.cardLook", padding: "sm", type: "card" }], reorderEvent: "REORDER_CARD", sortable: "@config.draggable", type: "data-list" }], direction: "vertical", gap: "none", type: "stack" }], className: "w-[300px] bg-[var(--color-surface-subtle)] rounded-lg overflow-hidden", look: "elevated", padding: "none", type: "card" }], scrollX: true, type: "data-grid" }, ["if", ["=", "@config.addPlacement", "floating"], { action: "ADD_CARD", icon: "plus", label: "Add card", type: "floating-action-button", variant: "primary" }, { children: [], gap: "none", type: "stack" }]], className: "h-full", direction: "vertical", gap: "md", type: "stack" },
  pageSize: 10,
  searchField: "title",
  searchPlaceholder: "Search…",
  showHeader: false,
  title: "Board",
  viewerRole: "",
} as HomeSLDemoCrmBoardConfig;

export interface HomeSLDemoCrmBoardLogicReturn {
  state: HomeSLDemoCrmBoardState;
  data: Record<string, EntityRow[]>;
  lastPayload: EventPayload | undefined;
  lastEvent: HomeSLDemoCrmBoardEvent | null;
  slotWriter: Partial<Record<HomeSLDemoCrmBoardSlot, HomeSLDemoCrmBoardEvent | null>>;
  loading: boolean;
  pendingEvent: HomeSLDemoCrmBoardEvent | null;
  pendingFrom: string | null;
  error: string | null;
  fields: Partial<HomeSLDemoCrmDeal>;
  dispatch: (event: string, payload?: EventPayload) => Promise<EventResponse>;
  actions: { [K in keyof HomeSLDemoCrmBoardEventPayloadMap]: (payload?: HomeSLDemoCrmBoardEventPayloadMap[K]) => Promise<EventResponse> };
  config: HomeSLDemoCrmBoardConfig;
}

const __CASCADE_EVENTS = new Set<string>(['MOVE_CARD', 'TOUR_RESET', 'TOUR_STEP']);

const __LEG_EVENTS: ReadonlySet<string> = new Set(['BoardItemDeleted', 'BoardItemsSaved', 'CLOSE_CARD', 'DELETE_CARD', 'INIT', 'MOVE_CARD', 'REFETCH_FILTER', 'REFETCH_PAGE', 'REFETCH_QUERY', 'REORDER_POSITION', 'SAVE_CARD']);

export function useHomeSLDemoCrmBoardLogic(config?: Partial<HomeSLDemoCrmBoardConfig>): HomeSLDemoCrmBoardLogicReturn {
  const mergedConfig: HomeSLDemoCrmBoardConfig = { ...DEFAULT_HOME_SL_DEMO_CRM_BOARD_CONFIG, ...(config ?? {}) };

  // Orbital bridge: event bus + server communication
  const { sendEvent, eventBus } = useOrbitalBridge({ basePath: '/api/home-sl-demo-crm-board', orbitalName: 'HomeSL', traitName: 'HomeSLDemoCrmBoard' });

  // Internal state with reducer
  const [internalState, dispatchReducer] = useReducer(homeSLDemoCrmBoardReducer, initialState);
  const { machineState: state, data, lastPayload, lastEvent, slotWriter, loading, pendingEvent, pendingFrom, error, fields: _initialFields } = internalState;
  const fields = _initialFields;
  const internalStateRef = useRef(internalState);
  internalStateRef.current = internalState;

  const cascadeReceivedRef = useRef<TraitStateSnapshot['cascadeReceived']>([]);

  const machineStateRef = useRef(state);
  machineStateRef.current = state;

  const fieldsRef = useRef<EntityRow>({ ...fields });
  fieldsRef.current = { ...fields };
  const commandPump = useMemo(() => createCommandSendPump(), []);

  // Actor model: sequential event queue (prevents cross-trait race conditions)
  const eventQueueRef = useRef<Array<{ event: string; payload?: EventPayload; settle?: () => void }>>([]);
  const processingRef = useRef(false);

  const { t: __t, locale: __locale } = useTranslate();
  // Transpiled client-side effects (Phase 4)
  // Each (fromState, event) transition's client effects are compiled to TypeScript.
  const executeTransitionEffects = useCallback((fromState: string, event: string, liveData: Record<string, EntityRow[]>, payload?: EventPayload): { fields: Record<string, EventPayloadValue | undefined>; newState: string | undefined; emits: Array<() => void> } => {
    const _fields: Record<string, EventPayloadValue | undefined> = {};
    const _emits: Array<() => void> = [];
    let _newState: string | undefined = undefined;
    const payloadObj = payload;
    const entity: EntityRow[] = liveData['HomeSLDemoCrmDeal'] ?? [];
    let fields: Record<string, EventPayloadValue | undefined> = entity[0] ?? {};


    if (fromState === 'loading' && event === 'INIT') {
      // loading → loading (on INIT)
      _newState = 'loading';
    } else if (fromState === 'loading' && event === 'BoardItemsLoaded') {
      // loading → viewing_board (on BoardItemsLoaded)
        _fields['boards'] = ([{ variant: "default" as const, key: "lead" as const, label: "Lead" as const }, { variant: "info" as const, label: "Qualified" as const, key: "qualified" as const }, { key: "proposal" as const, variant: "warning" as const, label: "Proposal" as const }, { variant: "success" as const, label: "Won" as const, key: "won" as const }]).map((col) => ({ label: getNestedValue(col, "label"), count: (((payloadObj?.data ?? []) as readonly EntityRow[]).filter((item) => (getNestedValue(item, "stage") === getNestedValue(col, "key")))).length, icon: (getNestedValue(col, "icon") ?? "circle"), variant: (getNestedValue(col, "variant") ?? "default"), key: getNestedValue(col, "key"), items: [...(((payloadObj?.data ?? []) as readonly EntityRow[]).filter((item) => (getNestedValue(item, "stage") === getNestedValue(col, "key"))))].sort((a, b) => { const av = (a as EntityRow)?.["position"], bv = (b as EntityRow)?.["position"]; if ((av as number) < (bv as number)) return -1; if ((av as number) > (bv as number)) return 1; return 0; }) }));
        fields = { ...(fields ?? {}), 'boards': _fields['boards'] } as Record<string, EventPayloadValue | undefined>;
        if (false) {

        } else {

        }

      _newState = 'viewing_board';
    } else if (fromState === 'loading' && event === 'BoardItemsLoadFailed') {
      // loading → error (on BoardItemsLoadFailed)
        _fields['errorMessage'] = payloadObj?.error;
        fields = { ...(fields ?? {}), 'errorMessage': _fields['errorMessage'] } as Record<string, EventPayloadValue | undefined>;
      _newState = 'error';
    } else if (fromState === 'loading' && event === 'BoardItemsSaved') {
      // loading → loading (on BoardItemsSaved)
      _newState = 'loading';
    } else if (fromState === 'loading' && event === 'BoardItemDeleted') {
      // loading → loading (on BoardItemDeleted)
      _newState = 'loading';
    } else if (fromState === 'loading' && event === 'BoardItemsSaveFailed') {
      // loading → error (on BoardItemsSaveFailed)
        _fields['errorMessage'] = payloadObj?.error;
        fields = { ...(fields ?? {}), 'errorMessage': _fields['errorMessage'] } as Record<string, EventPayloadValue | undefined>;
      _newState = 'error';
    } else if (fromState === 'viewing_board' && event === 'INIT') {
      // viewing_board → loading (on INIT)
      _newState = 'loading';
    } else if (fromState === 'viewing_board' && event === 'OPEN_CARD') {
      // viewing_board → viewing_card (on OPEN_CARD)
        _fields['currentId'] = payloadObj?.id;
        fields = { ...(fields ?? {}), 'currentId': _fields['currentId'] } as Record<string, EventPayloadValue | undefined>;
        _fields['currentTitle'] = payloadObj?.title;
        fields = { ...(fields ?? {}), 'currentTitle': _fields['currentTitle'] } as Record<string, EventPayloadValue | undefined>;
        _fields['currentDescription'] = payloadObj?.description;
        fields = { ...(fields ?? {}), 'currentDescription': _fields['currentDescription'] } as Record<string, EventPayloadValue | undefined>;
        _fields['currentStage'] = payloadObj?.stage;
        fields = { ...(fields ?? {}), 'currentStage': _fields['currentStage'] } as Record<string, EventPayloadValue | undefined>;
        _fields['currentNotes'] = payloadObj?.notes;
        fields = { ...(fields ?? {}), 'currentNotes': _fields['currentNotes'] } as Record<string, EventPayloadValue | undefined>;
        _fields['currentRow'] = payloadObj?.row;
        fields = { ...(fields ?? {}), 'currentRow': _fields['currentRow'] } as Record<string, EventPayloadValue | undefined>;
      _newState = 'viewing_card';
    } else if (fromState === 'viewing_board' && event === 'ADD_CARD') {
      // viewing_board → adding (on ADD_CARD)
        _fields['currentId'] = "";
        fields = { ...(fields ?? {}), 'currentId': _fields['currentId'] } as Record<string, EventPayloadValue | undefined>;
        _fields['currentTitle'] = "";
        fields = { ...(fields ?? {}), 'currentTitle': _fields['currentTitle'] } as Record<string, EventPayloadValue | undefined>;
        _fields['currentDescription'] = "";
        fields = { ...(fields ?? {}), 'currentDescription': _fields['currentDescription'] } as Record<string, EventPayloadValue | undefined>;
        _fields['currentStage'] = "";
        fields = { ...(fields ?? {}), 'currentStage': _fields['currentStage'] } as Record<string, EventPayloadValue | undefined>;
        _fields['currentNotes'] = "";
        fields = { ...(fields ?? {}), 'currentNotes': _fields['currentNotes'] } as Record<string, EventPayloadValue | undefined>;
      _newState = 'adding';
    } else if (fromState === 'viewing_board' && event === 'MOVE_CARD') {
      // viewing_board → viewing_board (on MOVE_CARD)
      _newState = 'viewing_board';
    } else if (fromState === 'viewing_board' && event === 'REORDER_CARD') {
      // viewing_board → viewing_board (on REORDER_CARD)
      _newState = 'viewing_board';
    } else if (fromState === 'viewing_board' && event === 'REORDER_POSITION') {
      // viewing_board → viewing_board (on REORDER_POSITION)
      _newState = 'viewing_board';
    } else if (fromState === 'viewing_board' && event === 'BoardItemsSaved') {
      // viewing_board → viewing_board (on BoardItemsSaved)
      _newState = 'viewing_board';
    } else if (fromState === 'viewing_board' && event === 'REFETCH_QUERY') {
      // viewing_board → viewing_board (on REFETCH_QUERY)
      _newState = 'viewing_board';
    } else if (fromState === 'viewing_board' && event === 'REFETCH_FILTER') {
      // viewing_board → viewing_board (on REFETCH_FILTER)
      _newState = 'viewing_board';
    } else if (fromState === 'viewing_board' && event === 'REFETCH_PAGE') {
      // viewing_board → viewing_board (on REFETCH_PAGE)
      _newState = 'viewing_board';
    } else if (fromState === 'viewing_board' && event === 'BoardItemsLoaded') {
      // viewing_board → viewing_board (on BoardItemsLoaded)
        _fields['boards'] = ([{ variant: "default" as const, key: "lead" as const, label: "Lead" as const }, { variant: "info" as const, label: "Qualified" as const, key: "qualified" as const }, { key: "proposal" as const, label: "Proposal" as const, variant: "warning" as const }, { variant: "success" as const, key: "won" as const, label: "Won" as const }]).map((col) => ({ items: [...(((payloadObj?.data ?? []) as readonly EntityRow[]).filter((item) => (getNestedValue(item, "stage") === getNestedValue(col, "key"))))].sort((a, b) => { const av = (a as EntityRow)?.["position"], bv = (b as EntityRow)?.["position"]; if ((av as number) < (bv as number)) return -1; if ((av as number) > (bv as number)) return 1; return 0; }), variant: (getNestedValue(col, "variant") ?? "default"), icon: (getNestedValue(col, "icon") ?? "circle"), key: getNestedValue(col, "key"), count: (((payloadObj?.data ?? []) as readonly EntityRow[]).filter((item) => (getNestedValue(item, "stage") === getNestedValue(col, "key")))).length, label: getNestedValue(col, "label") }));
        fields = { ...(fields ?? {}), 'boards': _fields['boards'] } as Record<string, EventPayloadValue | undefined>;
      _newState = 'viewing_board';
    } else if (fromState === 'viewing_board' && event === 'BoardItemsSaveFailed') {
      // viewing_board → viewing_board (on BoardItemsSaveFailed)
        _fields['errorMessage'] = payloadObj?.error;
        fields = { ...(fields ?? {}), 'errorMessage': _fields['errorMessage'] } as Record<string, EventPayloadValue | undefined>;
      _newState = 'viewing_board';
    } else if (fromState === 'viewing_board' && event === 'BoardItemsLoadFailed') {
      // viewing_board → viewing_board (on BoardItemsLoadFailed)
        _fields['errorMessage'] = payloadObj?.error;
        fields = { ...(fields ?? {}), 'errorMessage': _fields['errorMessage'] } as Record<string, EventPayloadValue | undefined>;
      _newState = 'viewing_board';
    } else if (fromState === 'viewing_card' && event === 'INIT') {
      // viewing_card → loading (on INIT)
      _newState = 'loading';
    } else if (fromState === 'viewing_card' && event === 'CLOSE_CARD') {
      // viewing_card → loading (on CLOSE_CARD)
      _newState = 'loading';
    } else if (fromState === 'viewing_card' && event === 'MOVE_CARD') {
      // viewing_card → loading (on MOVE_CARD)
      _newState = 'loading';
    } else if (fromState === 'viewing_card' && event === 'DELETE_CARD') {
      // viewing_card → loading (on DELETE_CARD)
      _newState = 'loading';
    } else if (fromState === 'adding' && event === 'INIT') {
      // adding → loading (on INIT)
      _newState = 'loading';
    } else if (fromState === 'adding' && event === 'SAVE_CARD') {
      // adding → loading (on SAVE_CARD)
      _newState = 'loading';
    } else if (fromState === 'adding' && event === 'CANCEL_ADD') {
      // adding → viewing_board (on CANCEL_ADD)
      _newState = 'viewing_board';
    } else if (fromState === 'error' && event === 'INIT') {
      // error → loading (on INIT)
      _newState = 'loading';
    }
    return { fields: _fields, newState: _newState, emits: _emits };
  }, [eventBus]);

  // Per-transition render-ui presence map (codegen).
  // Used by dispatch to skip lastPayload/lastEvent updates for
  // transitions whose only effects are server-side or set/emit —
  // those don't change the rendered JSX, so the view should keep
  // the prior frame's payload until a render-bearing transition
  // (e.g. fetch.emit.success cascade) lands.
  const getRenderUiSlots = useCallback((fromState: string, event: string): { write: HomeSLDemoCrmBoardSlot[]; clear: HomeSLDemoCrmBoardSlot[] } => {
    switch (fromState) {
      case 'adding':
        switch (event) {
          case 'CANCEL_ADD': return { write: ['main'] as HomeSLDemoCrmBoardSlot[], clear: [] as HomeSLDemoCrmBoardSlot[] };
          case 'INIT': return { write: ['main'] as HomeSLDemoCrmBoardSlot[], clear: [] as HomeSLDemoCrmBoardSlot[] };
          default: return { write: [], clear: [] };
        }
      case 'error':
        switch (event) {
          case 'INIT': return { write: ['main'] as HomeSLDemoCrmBoardSlot[], clear: [] as HomeSLDemoCrmBoardSlot[] };
          default: return { write: [], clear: [] };
        }
      case 'loading':
        switch (event) {
          case 'BoardItemsLoadFailed': return { write: ['main'] as HomeSLDemoCrmBoardSlot[], clear: [] as HomeSLDemoCrmBoardSlot[] };
          case 'BoardItemsLoaded': return { write: ['main'] as HomeSLDemoCrmBoardSlot[], clear: [] as HomeSLDemoCrmBoardSlot[] };
          case 'BoardItemsSaveFailed': return { write: ['main'] as HomeSLDemoCrmBoardSlot[], clear: [] as HomeSLDemoCrmBoardSlot[] };
          case 'BoardItemsSaved': return { write: ['main'] as HomeSLDemoCrmBoardSlot[], clear: [] as HomeSLDemoCrmBoardSlot[] };
          case 'INIT': return { write: ['main'] as HomeSLDemoCrmBoardSlot[], clear: [] as HomeSLDemoCrmBoardSlot[] };
          default: return { write: [], clear: [] };
        }
      case 'viewing_board':
        switch (event) {
          case 'ADD_CARD': return { write: ['main'] as HomeSLDemoCrmBoardSlot[], clear: [] as HomeSLDemoCrmBoardSlot[] };
          case 'BoardItemsLoadFailed': return { write: ['toast'] as HomeSLDemoCrmBoardSlot[], clear: [] as HomeSLDemoCrmBoardSlot[] };
          case 'BoardItemsSaveFailed': return { write: ['toast'] as HomeSLDemoCrmBoardSlot[], clear: [] as HomeSLDemoCrmBoardSlot[] };
          case 'INIT': return { write: ['main'] as HomeSLDemoCrmBoardSlot[], clear: [] as HomeSLDemoCrmBoardSlot[] };
          case 'OPEN_CARD': return { write: ['main'] as HomeSLDemoCrmBoardSlot[], clear: [] as HomeSLDemoCrmBoardSlot[] };
          default: return { write: [], clear: [] };
        }
      case 'viewing_card':
        switch (event) {
          case 'CLOSE_CARD': return { write: ['main'] as HomeSLDemoCrmBoardSlot[], clear: [] as HomeSLDemoCrmBoardSlot[] };
          case 'INIT': return { write: ['main'] as HomeSLDemoCrmBoardSlot[], clear: [] as HomeSLDemoCrmBoardSlot[] };
          case 'MOVE_CARD': return { write: ['main'] as HomeSLDemoCrmBoardSlot[], clear: [] as HomeSLDemoCrmBoardSlot[] };
          default: return { write: [], clear: [] };
        }
      default:
        return { write: [], clear: [] };
    }
  }, []);

  /**
   * Dispatch an event through the orbital bridge.
   * Bridge emits lifecycle events on the bus, forwards to server.
   * Client effects are transpiled at compile time (Phase 4).
   */
  const dispatch = useCallback(async (event: string, __rawPayload?: EventPayload): Promise<EventResponse> => {
    const payload: EventPayload | undefined =
      __rawPayload !== undefined && ('entityId' in __rawPayload || 'id' in __rawPayload)
        ? __rawPayload
        : config?.entityId !== undefined
          ? { ...(__rawPayload ?? {}), entityId: config.entityId }
          : __rawPayload;
    const prevState = machineStateRef.current;
    // T9 optimistic dispatch: the fields mirror IS the [runtime] entity
    // row, so guard + to-state resolve locally on the same data the
    // server would read. Commit first, POST after; reconcile below.
    const liveData: Record<string, EntityRow[]> = { 'HomeSLDemoCrmDeal': [fieldsRef.current as EntityRow] };
    const _optimistic = executeTransitionEffects(prevState, event, liveData, payload);
    if (_optimistic.newState === undefined) {
      // Guard rejected or no transition defined — mirrors the server's
      // rejection, without the round trip.
      const errorMsg = `No transition defined for ${event} from ${prevState} (or guard rejected)`;
      dispatchReducer({ type: 'EVENT_ERROR', error: errorMsg });
      return { success: false, transitioned: false, states: {}, emittedEvents: [], error: errorMsg } as EventResponse;
    }
    const _renderSlots = getRenderUiSlots(prevState, event);
    const _renderBearing = _renderSlots.write.length > 0 || _renderSlots.clear.length > 0;
    // Snapshot the touched fields' prior values for a reconcile rollback.
    const _snapshot: Record<string, EventPayloadValue | undefined> = {};
    for (const _k of Object.keys(_optimistic.fields)) { _snapshot[_k] = fieldsRef.current[_k]; }
    dispatchReducer({
      type: 'EVENT_SUCCESS',
      event: event as HomeSLDemoCrmBoardEvent,
      newState: _optimistic.newState,
      data: {},
      payload,
      fields: _optimistic.fields,
      renderBearing: _renderBearing,
      renderWrite: _renderSlots.write,
      renderClear: _renderSlots.clear,
    });
    if (__CASCADE_EVENTS.has(event)) {
      cascadeReceivedRef.current.push({ event, ...(payload !== undefined ? { payload } : {}), timestamp: Date.now() });
    }
    machineStateRef.current = _optimistic.newState as typeof machineStateRef.current;
    fieldsRef.current = { ...fieldsRef.current, ..._optimistic.fields } as EntityRow;
    for (const _emit of _optimistic.emits) _emit();

    if (__LEG_EVENTS.has(event)) {
    // Server leg: ordered by the pump, never blocking the drain.
    commandPump.enqueue(async () => {
      try {
        const response = await sendEvent(event, payload, prevState);
        if (response.success) {
          // Agreement — merge any fetch/persist results the local commit
          // couldn't supply (same reducer action, deferred leg).
          if (response.data && Object.keys(response.data).length > 0) {
            dispatchReducer({
              type: 'EVENT_SUCCESS',
              event: event as HomeSLDemoCrmBoardEvent,
              newState: machineStateRef.current,
              data: response.data,
              renderBearing: false,
            });
          }
        } else {
          // Rejection or state disagreement — roll back to the
          // pre-dispatch snapshot; the server stays authoritative.
          dispatchReducer({
            type: 'EVENT_SUCCESS',
            event: event as HomeSLDemoCrmBoardEvent,
            newState: prevState,
            data: {},
            fields: _snapshot,
            renderBearing: false,
          });
          machineStateRef.current = prevState;
          fieldsRef.current = { ...fieldsRef.current, ..._snapshot } as EntityRow;
          dispatchReducer({ type: 'EVENT_ERROR', error: response.error ?? `server state mismatch: expected ${_optimistic.newState}, got ${response.states?.['HomeSLDemoCrmBoard'] ?? '(unchanged)'}` });
        }
      } catch (err) {
        // Pump isolates the rejection; surface it without rolling
        // back — local state may simply be ahead of a reconnecting server.
        dispatchReducer({ type: 'EVENT_ERROR', error: err instanceof Error ? err.message : 'Network error' });
      }
    });
    }
    return { success: true, transitioned: true, states: { 'HomeSLDemoCrmBoard': _optimistic.newState }, emittedEvents: [] } as EventResponse;
  }, [sendEvent, executeTransitionEffects, commandPump, config?.entityId]);

  // Actor model: drain event queue one at a time
  const drainQueue = useCallback(async () => {
    if (processingRef.current) return;
    processingRef.current = true;
    try {
      while (eventQueueRef.current.length > 0) {
        const entry = eventQueueRef.current.shift()!;
        try { await dispatch(entry.event, entry.payload); } finally { entry.settle?.(); }
      }
    } finally {
      processingRef.current = false;
    }
  }, [dispatch]);

  // Actor model: enqueue event and start drain
  const enqueueEvent = useCallback((event: string, payload?: EventPayload) => {
    return new Promise<void>((settle) => {
    eventQueueRef.current.push({ event, payload, settle });
    void drainQueue();
    });
  }, [drainQueue]);

  // Action creators (typed per-event)
  const actions: { [K in keyof HomeSLDemoCrmBoardEventPayloadMap]: (payload?: HomeSLDemoCrmBoardEventPayloadMap[K]) => Promise<EventResponse> } = {
    'INIT': () => dispatch('INIT'),
    'BoardItemsLoaded': (payload?: HomeSLDemoCrmBoardEventPayloadMap['BoardItemsLoaded']) => dispatch('BoardItemsLoaded', payload),
    'BoardItemsLoadFailed': (payload?: HomeSLDemoCrmBoardEventPayloadMap['BoardItemsLoadFailed']) => dispatch('BoardItemsLoadFailed', payload),
    'BoardItemsSaved': (payload?: HomeSLDemoCrmBoardEventPayloadMap['BoardItemsSaved']) => dispatch('BoardItemsSaved', payload),
    'BoardItemDeleted': (payload?: HomeSLDemoCrmBoardEventPayloadMap['BoardItemDeleted']) => dispatch('BoardItemDeleted', payload),
    'BoardItemsSaveFailed': (payload?: HomeSLDemoCrmBoardEventPayloadMap['BoardItemsSaveFailed']) => dispatch('BoardItemsSaveFailed', payload),
    'OPEN_CARD': (payload?: HomeSLDemoCrmBoardEventPayloadMap['OPEN_CARD']) => dispatch('OPEN_CARD', payload),
    'ADD_CARD': () => dispatch('ADD_CARD'),
    'MOVE_CARD': (payload?: HomeSLDemoCrmBoardEventPayloadMap['MOVE_CARD']) => dispatch('MOVE_CARD', payload),
    'REORDER_CARD': (payload?: HomeSLDemoCrmBoardEventPayloadMap['REORDER_CARD']) => dispatch('REORDER_CARD', payload),
    'REORDER_POSITION': (payload?: HomeSLDemoCrmBoardEventPayloadMap['REORDER_POSITION']) => dispatch('REORDER_POSITION', payload),
    'REFETCH_QUERY': (payload?: HomeSLDemoCrmBoardEventPayloadMap['REFETCH_QUERY']) => dispatch('REFETCH_QUERY', payload),
    'REFETCH_FILTER': (payload?: HomeSLDemoCrmBoardEventPayloadMap['REFETCH_FILTER']) => dispatch('REFETCH_FILTER', payload),
    'REFETCH_PAGE': (payload?: HomeSLDemoCrmBoardEventPayloadMap['REFETCH_PAGE']) => dispatch('REFETCH_PAGE', payload),
    'CLOSE_CARD': () => dispatch('CLOSE_CARD'),
    'DELETE_CARD': (payload?: HomeSLDemoCrmBoardEventPayloadMap['DELETE_CARD']) => dispatch('DELETE_CARD', payload),
    'SAVE_CARD': (payload?: HomeSLDemoCrmBoardEventPayloadMap['SAVE_CARD']) => dispatch('SAVE_CARD', payload),
    'CANCEL_ADD': () => dispatch('CANCEL_ADD'),
    'EDIT_CARD': (payload?: HomeSLDemoCrmBoardEventPayloadMap['EDIT_CARD']) => dispatch('EDIT_CARD', payload),
  };

  useUIEvents(enqueueEvent, 'HomeSL.HomeSLDemoCrmBoard', ['INIT', 'BoardItemsLoaded', 'BoardItemsLoadFailed', 'BoardItemsSaved', 'BoardItemDeleted', 'BoardItemsSaveFailed', 'OPEN_CARD', 'ADD_CARD', 'MOVE_CARD', 'REORDER_CARD', 'REORDER_POSITION', 'REFETCH_QUERY', 'REFETCH_FILTER', 'REFETCH_PAGE', 'CLOSE_CARD', 'DELETE_CARD', 'SAVE_CARD', 'CANCEL_ADD'] as const, eventBus);

  // Verifier snapshot registration (VG Foundation 1)
  useEffect(() => {
    const unregister = registerTraitSnapshot('HomeSLDemoCrmBoard', (): TraitStateSnapshot => ({
      traitName: 'HomeSLDemoCrmBoard',
      currentState: internalStateRef.current.machineState,
      states: ['loading', 'viewing_board', 'viewing_card', 'adding', 'error'],
      events: ['INIT', 'BoardItemsLoaded', 'BoardItemsLoadFailed', 'BoardItemsSaved', 'BoardItemDeleted', 'BoardItemsSaveFailed', 'OPEN_CARD', 'ADD_CARD', 'MOVE_CARD', 'REORDER_CARD', 'REORDER_POSITION', 'REFETCH_QUERY', 'REFETCH_FILTER', 'REFETCH_PAGE', 'CLOSE_CARD', 'DELETE_CARD', 'SAVE_CARD', 'CANCEL_ADD', 'EDIT_CARD'],
      data: internalStateRef.current.data,
      lastPayload: internalStateRef.current.lastPayload,
      lastEventDispatched: internalStateRef.current.lastEvent !== null
        ? {
            event: internalStateRef.current.lastEvent,
            ...(internalStateRef.current.lastPayload !== undefined
              ? { payload: internalStateRef.current.lastPayload }
              : {}),
            timestamp: Date.now(),
          }
        : undefined,
      cascadeReceived: cascadeReceivedRef.current,
    }));
    return unregister;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Auto-init on mount
  useEffect(() => {
    const __rp = Object.fromEntries(Object.entries(config?.routeParams ?? {}).filter(([, v]) => v !== undefined));
    const __init = { ...__rp, ...(config?.initPayload ?? {}), ...(config?.entityId !== undefined ? { entityId: config.entityId } : {}) };
    dispatch('INIT', Object.keys(__init).length > 0 ? __init : undefined);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Cross-trait event listeners (gap #13: qualified bus keys)
  useEffect(() => {
    // Listen for 'TOUR_STEP' (from HomeSLDemoCrmTour) → trigger 'MOVE_CARD'
    const unsub_tour_step_from_homesldemocrmtour_to_move_card = eventBus.on('UI:HomeSL.HomeSLDemoCrmTour.TOUR_STEP', (event: BusEvent) => {
      if (!(((event.payload as EventPayload | undefined)?.event === "QUALIFY_NORTHWIND"))) return;
      if (!claimDelivery(event.source, 'HomeSL.HomeSLDemoCrmBoard', 'MOVE_CARD')) return;
      enqueueEvent('MOVE_CARD', { id: "deal-1", targetGroup: "qualified" } as EventPayload);
    });
    // Listen for 'TOUR_STEP' (from HomeSLDemoCrmTour) → trigger 'MOVE_CARD'
    const unsub_tour_step_from_homesldemocrmtour_to_move_card_2 = eventBus.on('UI:HomeSL.HomeSLDemoCrmTour.TOUR_STEP', (event: BusEvent) => {
      if (!(((event.payload as EventPayload | undefined)?.event === "PROPOSE_NORTHWIND"))) return;
      if (!claimDelivery(event.source, 'HomeSL.HomeSLDemoCrmBoard', 'MOVE_CARD')) return;
      enqueueEvent('MOVE_CARD', { id: "deal-1", targetGroup: "proposal" } as EventPayload);
    });
    // Listen for 'TOUR_STEP' (from HomeSLDemoCrmTour) → trigger 'MOVE_CARD'
    const unsub_tour_step_from_homesldemocrmtour_to_move_card_3 = eventBus.on('UI:HomeSL.HomeSLDemoCrmTour.TOUR_STEP', (event: BusEvent) => {
      if (!(((event.payload as EventPayload | undefined)?.event === "WIN_BLUEBIRD"))) return;
      if (!claimDelivery(event.source, 'HomeSL.HomeSLDemoCrmBoard', 'MOVE_CARD')) return;
      enqueueEvent('MOVE_CARD', { id: "deal-3", targetGroup: "won" } as EventPayload);
    });
    // Listen for 'TOUR_STEP' (from HomeSLDemoCrmTour) → trigger 'MOVE_CARD'
    const unsub_tour_step_from_homesldemocrmtour_to_move_card_4 = eventBus.on('UI:HomeSL.HomeSLDemoCrmTour.TOUR_STEP', (event: BusEvent) => {
      if (!(((event.payload as EventPayload | undefined)?.event === "WIN_NORTHWIND"))) return;
      if (!claimDelivery(event.source, 'HomeSL.HomeSLDemoCrmBoard', 'MOVE_CARD')) return;
      enqueueEvent('MOVE_CARD', { id: "deal-1", targetGroup: "won" } as EventPayload);
    });
    // Listen for 'TOUR_RESET' (from HomeSLDemoCrmTour) → trigger 'MOVE_CARD'
    const unsub_tour_reset_from_homesldemocrmtour_to_move_card = eventBus.on('UI:HomeSL.HomeSLDemoCrmTour.TOUR_RESET', (event: BusEvent) => {
      if (!claimDelivery(event.source, 'HomeSL.HomeSLDemoCrmBoard', 'MOVE_CARD')) return;
      enqueueEvent('MOVE_CARD', { id: "deal-1", targetGroup: "lead" } as EventPayload);
    });
    // Listen for 'TOUR_RESET' (from HomeSLDemoCrmTour) → trigger 'MOVE_CARD'
    const unsub_tour_reset_from_homesldemocrmtour_to_move_card_2 = eventBus.on('UI:HomeSL.HomeSLDemoCrmTour.TOUR_RESET', (event: BusEvent) => {
      if (!claimDelivery(event.source, 'HomeSL.HomeSLDemoCrmBoard', 'MOVE_CARD')) return;
      enqueueEvent('MOVE_CARD', { id: "deal-3", targetGroup: "proposal" } as EventPayload);
    });
    return () => {
      if (typeof unsub_tour_step_from_homesldemocrmtour_to_move_card === 'function') unsub_tour_step_from_homesldemocrmtour_to_move_card();
      if (typeof unsub_tour_step_from_homesldemocrmtour_to_move_card_2 === 'function') unsub_tour_step_from_homesldemocrmtour_to_move_card_2();
      if (typeof unsub_tour_step_from_homesldemocrmtour_to_move_card_3 === 'function') unsub_tour_step_from_homesldemocrmtour_to_move_card_3();
      if (typeof unsub_tour_step_from_homesldemocrmtour_to_move_card_4 === 'function') unsub_tour_step_from_homesldemocrmtour_to_move_card_4();
      if (typeof unsub_tour_reset_from_homesldemocrmtour_to_move_card === 'function') unsub_tour_reset_from_homesldemocrmtour_to_move_card();
      if (typeof unsub_tour_reset_from_homesldemocrmtour_to_move_card_2 === 'function') unsub_tour_reset_from_homesldemocrmtour_to_move_card_2();
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventBus, dispatch]);

  mergedConfig.columnLabels = mergedConfig.columnLabels ?? ([__t("std-almadar-demo-crm-page:stage.lead"), __t("std-almadar-demo-crm-page:stage.qualified"), __t("std-almadar-demo-crm-page:stage.proposal"), __t("std-almadar-demo-crm-page:stage.won")]);
  return {
    state,
    data,
    lastPayload,
    lastEvent,
    slotWriter,
    loading,
    pendingEvent,
    pendingFrom,
    error,
    fields,
    dispatch,
    actions,
    config: mergedConfig,
  };
}
